// State/IndicatorsProvider.ts
import React, { useState, useCallback, useRef, ReactNode, FC } from 'react';
import { IndicatorsContext, Indicator } from './IndicatorsContext';
import { route, GoalType } from '../utils/apiRoutes';
import { sortIndicators } from '../utils/sortUtils';
import { transformIndicatorPreview } from '../utils/transformApiResponse';
import { apiCache } from '../utils/apiCache';

const IndicatorsProvider: FC<{ children: ReactNode }> = (props) => {
  const [indicators, setIndicators] = useState<Indicator[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [initialLoading, setInitialLoading] = useState<boolean>(true);
  const fetchedGoalIdsRef = useRef<Set<string>>(new Set());
  const typeRequestIdRef = useRef(0);

  const fetchAllIndicators = useCallback(async () => {
    setLoading(true);
    try {
      const [unResponse, euResponse] = await Promise.all([
        fetch(route.indicatorsList('un')),
        fetch(route.indicatorsList('eu'))
      ]);
      
      if (!unResponse.ok || !euResponse.ok) {
        throw new Error('Failed to fetch indicators list');
      }
      
      const [unData, euData] = await Promise.all([
        unResponse.json(),
        euResponse.json()
      ]);
      
      const allIndicatorsMinimal = [
        ...(Array.isArray(unData) ? unData : []),
        ...(Array.isArray(euData) ? euData : [])
      ];
      
      if (allIndicatorsMinimal.length === 0) {
        setIndicators([]);
        setLoading(false);
        setInitialLoading(false);
        return;
      }
      
      // Step 2: Fetch full preview data for each indicator
      const previewPromises = allIndicatorsMinimal.map(async (item: { id: number; title: string }) => {
        try {
          const previewResponse = await fetch(route.indicatorPreview(item.id, false));
          if (previewResponse.ok) {
            const preview = await previewResponse.json();
            return transformIndicatorPreview(preview);
          }
          return null;
        } catch (error) {
          console.error(`Error fetching preview for indicator ${item.id}:`, error);
          return null;
        }
      });
      
      const previewResults = await Promise.all(previewPromises);
      const validIndicators = previewResults.filter((ind): ind is Indicator => ind !== null);
      
      const sorted = sortIndicators(validIndicators) as Indicator[];
      setIndicators(sorted);
    } catch (error) {
      console.error('Error fetching all indicators:', error);
      setIndicators([]);
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  }, []);

  const fetchAllIndicatorsByType = useCallback(async (goalType: GoalType) => {
    const requestId = ++typeRequestIdRef.current;
    const pageSize = 100;

    // Clear the previous framework immediately so UN and EU data never share a render.
    setIndicators([]);
    setLoading(true);
    setInitialLoading(true);
    try {
      const allPreviews: any[] = [];
      let offset = 0;
      let total = 0;

      // Fetch every WordPress page. The profile and catalogue must not stop at the
      // old first-page limit of 50 indicators.
      do {
        const batchUrl = route.indicatorsPreviewBatch(
          goalType,
          undefined,
          undefined,
          pageSize,
          offset,
          false
        );
        const batchResponse = await fetch(batchUrl);

        if (!batchResponse.ok) {
          throw new Error(`Failed to fetch indicators batch: ${batchResponse.status}`);
        }

        const batchData = await batchResponse.json();
        const page = batchData.indicators || [];
        if (!Array.isArray(page)) {
          throw new Error('Invalid indicators batch response');
        }

        allPreviews.push(...page);
        total = Number(batchData.total) || allPreviews.length;
        if (page.length === 0) break;
        offset += page.length;
      } while (offset < total);

      if (requestId !== typeRequestIdRef.current) return;

      const transformedIndicators = allPreviews.map((preview: any) => transformIndicatorPreview(preview));
      const validIndicators = transformedIndicators.filter((ind): ind is Indicator => ind !== null);
      const sorted = sortIndicators(validIndicators) as Indicator[];

      setIndicators(sorted);
    } catch (error) {
      if (requestId !== typeRequestIdRef.current) return;
      console.error('Error fetching all indicators by type:', error);
      setIndicators([]);
    } finally {
      if (requestId === typeRequestIdRef.current) {
        setLoading(false);
        setInitialLoading(false);
      }
    }
  }, []);

  const fetchAllIndicatorsByGoal = useCallback(async (goalId: number, goalType: GoalType) => {
    // Always clear indicators first when switching goals to prevent stale data
    setIndicators([]);
    setLoading(true);
    
    // Check if already fetched using ref (but still clear state above)
    const cacheKey = `${goalId}-${goalType}`;
    if (fetchedGoalIdsRef.current.has(cacheKey)) {
      // Still fetch to ensure fresh data, but can optimize later with proper cache
      // For now, always fetch to ensure correct data
    }
    
    // Mark as fetching to prevent duplicate requests
    fetchedGoalIdsRef.current.add(cacheKey);
    
    try {
      // Use batch preview endpoint with goal_id filter - single call instead of N calls
      const batchUrl = route.indicatorsPreviewBatch(goalType, goalId, undefined, undefined, undefined, false);
      const batchResponse = await fetch(batchUrl);
      
      if (!batchResponse.ok) {
        throw new Error(`Failed to fetch indicators batch: ${batchResponse.status}`);
      }
      
      const batchData = await batchResponse.json();
      const indicatorsArray = batchData.indicators || [];
      
      // Transform batch response to Indicator format
      const transformedIndicators = indicatorsArray.map((preview: any) => transformIndicatorPreview(preview));
      const validIndicators = transformedIndicators.filter((ind: Indicator | null): ind is Indicator => ind !== null);
      
      const sorted = sortIndicators(validIndicators) as Indicator[];
      setIndicators(sorted);
      setLoading(false);
    } catch (error) {
      console.error(`Error fetching indicators for goal ${goalId}:`, error);
      setIndicators([]);
      // Remove from ref on error so it can be retried
      fetchedGoalIdsRef.current.delete(cacheKey);
      setLoading(false);
    }
  }, []); // No dependencies - use ref for tracking

  const fetchAllIndicatorsByTarget = useCallback(async (targetId: number, goalType: GoalType) => {
    setLoading(true);
    try {
      // Use batch preview endpoint with target_id filter - single call instead of N calls
      const batchUrl = route.indicatorsPreviewBatch(goalType, undefined, targetId, undefined, undefined, false);
      const batchResponse = await fetch(batchUrl);
      
      if (!batchResponse.ok) {
        throw new Error(`Failed to fetch indicators batch: ${batchResponse.status}`);
      }
      
      const batchData = await batchResponse.json();
      const indicatorsArray = batchData.indicators || [];
      
      if (indicatorsArray.length === 0) {
        setIndicators([]);
        fetchedGoalIdsRef.current.clear();
        setLoading(false);
        return;
      }
      
      // Transform batch response to Indicator format and ensure target_id is set
      const transformedIndicators = indicatorsArray.map((preview: any) => {
        const transformed = transformIndicatorPreview(preview);
        return { ...transformed, target_id: targetId };
      });
      const validIndicators = transformedIndicators.filter((ind: Indicator | null): ind is Indicator => ind !== null);
      
      const sorted = sortIndicators(validIndicators) as Indicator[];
      setIndicators(sorted);
      fetchedGoalIdsRef.current.clear();
      setLoading(false);
    } catch (error) {
      console.error(`Error fetching indicators for target ${targetId}:`, error);
      setIndicators([]);
      setLoading(false);
    }
  }, []);

  // Fetch a single indicator using preview endpoint.
  // Does not set global loading so that indicator list view keeps showing when switching indicators.
  const fetchSingleIndicator = useCallback(async (indicatorId: number, goalType: GoalType) => {
    try {
      const cacheKey = `indicator-preview-${indicatorId}`;
      
      // Check cache first
      const cached = apiCache.get<Indicator>(cacheKey);
      if (cached) {
        // Update the indicator in the list if it exists
        setIndicators(prev => {
          const index = prev.findIndex(i => i.id === indicatorId);
          if (index >= 0) {
            const updated = [...prev];
            updated[index] = cached;
            return updated;
          }
          return prev;
        });
        return cached;
      }
      
      // Fetch with deduplication
      const transformed = await apiCache.getOrCreatePending(cacheKey, async () => {
        const response = await fetch(route.indicatorPreview(indicatorId, false));
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        
        const preview = await response.json();
        const result = transformIndicatorPreview(preview);
        apiCache.set(cacheKey, result, 5 * 60 * 1000);
        return result;
      });
      
      // Update the indicator in the list if it exists
      setIndicators(prev => {
        const index = prev.findIndex(i => i.id === indicatorId);
        if (index >= 0) {
          const updated = [...prev];
          updated[index] = transformed;
          return updated;
        }
        return prev;
      });
      
      return transformed;
    } catch (error) {
      console.error(`Error fetching indicator ${indicatorId}:`, error);
      return null;
    }
  }, []);

  return React.createElement(
    IndicatorsContext.Provider,
    {
      value: {
        indicators,
        loading,
        initialLoading,
        fetchAllIndicators,
        fetchAllIndicatorsByType,
        fetchAllIndicatorsByGoal,
        fetchAllIndicatorsByTarget,
        fetchSingleIndicator,
      },
    },
    props.children
  );
};

export { IndicatorsProvider };
